import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import toast from '../shared/Toast';
import { paymentService } from '../../_services';
import './PaymentLinkModal.css';

const PaymentLinkModal = ({ show, onHide, paymentLinkData, bookingId, onPaymentComplete, onCheckPayment, clientEmail, clientName }) => {
    const [copied, setCopied] = useState(false);
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [checking, setChecking] = useState(false);
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [email, setEmail] = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);
    const [emailSent, setEmailSent] = useState(false);

    // Determine payment type from paymentLinkData
    const getPaymentType = () => {
        if (!paymentLinkData) return 'payment';
        const paymentType = paymentLinkData.paymentType?.toLowerCase();
        if (paymentType === 'klarna') return 'klarna';
        if (paymentType === 'clearpay') return 'clearpay';
        return 'payment';
    };

    const paymentType = getPaymentType();

    // Get payment type specific content
    const getPaymentTypeContent = () => {
        switch (paymentType) {
            case 'klarna':
                return {
                    title: 'Klarna Payment Link',
                    icon: '🛍️',
                    color: '#FFB3C7',
                    description: 'Pay in 3 interest-free instalments',
                    instruction: 'Share this link with your client to pay with Klarna'
                };
            case 'clearpay':
                return {
                    title: 'Clearpay Payment Link',
                    icon: '💳',
                    color: '#B2FCE4',
                    description: 'Pay in 4 interest-free instalments',
                    instruction: 'Share this link with your client to pay with Clearpay'
                };
            default:
                return {
                    title: 'Quick Pay (Link & QR)',
                    icon: '💰',
                    color: '#E3F2FD',
                    description: 'Secure payment link',
                    instruction: 'Share this link with your client to complete payment'
                };
        }
    };

    const content = getPaymentTypeContent();

    useEffect(() => {
        if (paymentLinkData && paymentLinkData.checkoutUrl) {
            // Generate QR code using a free API service
            const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(paymentLinkData.checkoutUrl)}`;
            setQrCodeUrl(qrApiUrl);
        }
    }, [paymentLinkData]);

    // Pre-fill email when clientEmail prop changes
    useEffect(() => {
        if (clientEmail) {
            setEmail(clientEmail);
        }
    }, [clientEmail]);

    // Reset email form state when modal is closed
    useEffect(() => {
        if (!show) {
            setShowEmailForm(false);
            setEmailSent(false);
        }
    }, [show]);

    const handleConfirmPayment = async () => {
        setChecking(true);
        try {
            // Call the parent's check payment function which uses Redux
            const booking = await onCheckPayment();
            
            if (booking && booking.isPaymentLinkPaid === 1) {
                toast.success('Payment confirmed!');
                // Call the callback to navigate to invoice
                if (onPaymentComplete) {
                    onPaymentComplete(booking);
                }
                onHide();
            } else {
                toast.warning('Payment not yet received. Please wait for the client to complete payment.');
            }
        } catch (error) {
            console.error('Error checking payment status:', error);
            toast.error('Client payment not received yet, please try again shortly');
        } finally {
            setChecking(false);
        }
    };

    const copyToClipboard = () => {
        if (paymentLinkData && paymentLinkData.checkoutUrl) {
            navigator.clipboard.writeText(paymentLinkData.checkoutUrl).then(() => {
                setCopied(true);
                toast.success('Link copied to clipboard!');
                setTimeout(() => setCopied(false), 2000);
            });
        }
    };

    const handleEmailClick = () => {
        setShowEmailForm(true);
    };

    const handleBackToMain = () => {
        setShowEmailForm(false);
    };

    const handleSendEmail = async () => {
        if (!email || !email.trim()) {
            toast.error('Please enter an email address');
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
            toast.error('Please enter a valid email address');
            return;
        }

        setSendingEmail(true);
        try {
            const response = await paymentService.sendPaymentLinkEmail({
                email: email.trim(),
                paymentLink: paymentLinkData?.checkoutUrl,
                amount: paymentLinkData?.amount,
                currency: paymentLinkData?.currency || 'GBP',
                bookingId: bookingId,
                clientName: clientName || ''
            });

            if (response.data && response.data.statusCode === 1) {
                setEmailSent(true);
                toast.success('Payment link sent successfully!');
                // Auto-hide email form after 2 seconds
                setTimeout(() => {
                    setShowEmailForm(false);
                    setEmailSent(false);
                }, 2000);
            } else {
                toast.error(response.data?.message || 'Failed to send email');
            }
        } catch (error) {
            console.error('Error sending payment link email:', error);
            toast.error('Failed to send email. Please try again.');
        } finally {
            setSendingEmail(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            centered
            className="payment-link-modal"
        >
            <Modal.Header closeButton className="modal-header-custom">
                <Modal.Title className="modal-title-custom">
                    <span className="payment-icon" style={{ background: content.color }}>{content.icon}</span>
                    <div>
                        <h4>{content.title}</h4>
                        <p className="payment-subtitle">{content.description}</p>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="modal-body-custom">
                <div className="payment-link-content">
                    {/* QR Code Section */}
                    <div className="qr-code-section">
                        <div className="qr-header">
                            <h5>📱 Scan to Pay</h5>
                            <p className="qr-subtitle">Quick and easy mobile payment</p>
                        </div>
                        {qrCodeUrl ? (
                            <div className="qr-code-wrapper">
                                <img 
                                    src={qrCodeUrl} 
                                    alt="Payment QR Code" 
                                    className="qr-code-image"
                                />
                            </div>
                        ) : (
                            <div className="qr-code-placeholder">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="sr-only">Loading...</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Payment Link Section */}
                    <div className="payment-link-section">
                        <div className="section-header">
                            <h5>🔗 Payment Link</h5>
                            <p className="section-subtitle">{content.instruction}</p>
                        </div>
                        
                        {!showEmailForm ? (
                            <div className="link-display-wrapper">
                                <div className="link-display">
                                    <input
                                        type="text"
                                        value={paymentLinkData?.checkoutUrl || ''}
                                        readOnly
                                        className="form-control link-input"
                                    />
                                    <button
                                        className="btn copy-btn"
                                        onClick={copyToClipboard}
                                    >
                                        {copied ? (
                                            <>
                                                <i className="fa fa-check"></i> Copied!
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-copy"></i> Copy
                                            </>
                                        )}
                                    </button>
                                    <button
                                        className="btn email-btn"
                                        onClick={handleEmailClick}
                                    >
                                        <i className="fa fa-envelope"></i> Email
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="email-form-wrapper">
                                <div className="email-form-header">
                                    <button
                                        className="btn back-btn"
                                        onClick={handleBackToMain}
                                    >
                                        <i className="fa fa-arrow-left"></i>
                                    </button>
                                    <h6>📧 Send Payment Link via Email</h6>
                                </div>
                                {emailSent ? (
                                    <div className="email-success">
                                        <div className="success-icon">
                                            <i className="fa fa-check-circle"></i>
                                        </div>
                                        <p>Email sent successfully to <strong>{email}</strong></p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="email-input-group">
                                            <label>Client Email Address</label>
                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="Enter client's email address"
                                                className="form-control email-input"
                                                disabled={sendingEmail}
                                            />
                                        </div>
                                        <div className="email-preview">
                                            <p className="preview-label">Preview:</p>
                                            <p className="preview-text">
                                                The client will receive an email with a secure payment link for <strong>{paymentLinkData?.currency || 'GBP'} {paymentLinkData?.amount?.toFixed(2) || '0.00'}</strong>
                                            </p>
                                        </div>
                                        <button
                                            className="btn send-email-btn"
                                            onClick={handleSendEmail}
                                            disabled={sendingEmail || !email.trim()}
                                        >
                                            {sendingEmail ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                                    Sending...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa fa-paper-plane"></i> Send Payment Link
                                                </>
                                            )}
                                        </button>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Payment Details */}
                        <div className="payment-details-card">
                            <div className="detail-row">
                                <span className="detail-label">💵 Amount</span>
                                <span className="detail-value">
                                    {paymentLinkData?.currency || 'GBP'} {paymentLinkData?.amount?.toFixed(2) || '0.00'}
                                </span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">📋 Booking ID</span>
                                <span className="detail-value booking-id">{bookingId}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer Instructions */}
                <div className="payment-link-footer">
                    <div className="info-box">
                        <i className="fa fa-info-circle"></i>
                        <div>
                            <strong>Next Steps:</strong>
                            <ol className="steps-list">
                                <li>Share the link or QR code with your client</li>
                                <li>Client completes the payment</li>
                                <li>Click "Confirm Payment" below to verify and update booking</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="modal-footer-custom">
                <button className="btn btn-secondary btn-custom" onClick={onHide}>
                    <i className="fa fa-times"></i> Close
                </button>
                <button
                    className="btn beu-blue-btn btn-custom"
                    onClick={handleConfirmPayment}
                    disabled={checking}
                >
                    {checking ? (
                        <>
                            <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                            Checking...
                        </>
                    ) : (
                        <>
                            <i className="fa fa-check-circle"></i> Confirm Payment Received
                        </>
                    )}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default PaymentLinkModal;